import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useForm from 'hooks/useForm';
import Alert from 'components/Base/Alert';
import { Col } from 'components/Base/Grid';
import LoginFooter, {
  ResendVerificationCodeButton,
} from 'components/Shared/LoginFooter';
import { useConfirmSignUpAmplifyMutation } from 'redux/apiSlice/authAmplify.slice';
import { useAppDispatch, useAppSelector } from 'redux/store';
import authSlice from 'redux/slices/auth.slice';
import { EFormIds, TVerificationData } from 'pages/public/Login/Login.types';
import { EVia, TAmplifyErrorPayload } from 'data/types/amplify.types';
import { RoutesPaths } from 'routes/Routes.types';
import Form from './components/Form';
import { useClearImpersonationData } from '../../../../../hooks/useClearImpersonationData';

const VerificationForm = () => {
  const navigate = useNavigate();
  const removeImpersonationData = useClearImpersonationData();
  const location = useLocation() as {
    state: { from: 'signup' | 'login' | 'reset' };
    search: string;
  };
  const { from } = location.state || {};
  const { form } = useForm<TVerificationData>();
  const [reFetch, { error }] = useConfirmSignUpAmplifyMutation();
  const signInUser = useAppSelector(state => state.authSlice.signInUser);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (!signInUser?.username) {
      return;
    }
    try {
      removeImpersonationData();
      const values: TVerificationData = await form.validateFields();
      setLoading(true);
      await reFetch({ username: signInUser.username, ...values }).unwrap();
      if (from === 'reset') {
        navigate(
          {
            pathname: `/${RoutesPaths.RESET_PASSWORD}`,
            search: location.search,
          },
          {
            state: { viaEmail: signInUser.via === EVia.EMAIL },
          },
        );
        dispatch(authSlice.actions.removeSignInUser());
      }
      // Here will be triggered Amplify Hub listener in PublicRoutes.tsx file
    } catch (e) {
      setLoading(false);
    }
  };

  return (
    <Col>
      <Alert
        type="error"
        message={(error as TAmplifyErrorPayload)?.data?.message}
      />
      <Form form={form} />
      <LoginFooter
        mode={from === 'signup' ? 'SIGN_UP_VERIFICATION' : 'VERIFICATION'}
        onClick={handleClick}
        onLinkClick={() => dispatch(authSlice.actions.removeSignInUser())}
        loading={loading}
        formId={EFormIds.VERIFICATION}
      >
        <ResendVerificationCodeButton />
      </LoginFooter>
    </Col>
  );
};

export default VerificationForm;
